import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { TextareaComponent } from '@components/textarea';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { StarRatingResult } from '../star-rating-result';

@Component({
  selector: 'app-star-rating-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    TextareaComponent,
    PrimaryButton,
    SecondaryButton,
  ],
  templateUrl: './star-rating-modal.html',
  styleUrl: './star-rating-modal.scss',
})
export class StarRatingModal {
  /** Whether the dialog is visible. */
  @Input() visible = false;

  /** The initial rating value to pre-select. */
  @Input() initialValue = 0;

  /** The initial comment to pre-fill. */
  @Input() initialComment = '';

  /** Emits when dialog visibility changes. */
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Emits the confirmed rating and comment. */
  @Output() confirmed = new EventEmitter<StarRatingResult>();

  readonly commentMinLength = 20;
  readonly commentMaxLength = 255;

  readonly starsArray = [1, 2, 3, 4, 5];
  readonly hoveredStar = signal(0);
  readonly selectedStar = signal(0);
  readonly comment = signal('');

  readonly trimmedComment = computed(() => (this.comment() || '').trim());
  readonly commentLength = computed(() => (this.comment() || '').length);
  readonly isCommentTooShort = computed(
    () => this.trimmedComment().length < this.commentMinLength,
  );
  readonly isCommentTooLong = computed(() => this.commentLength() > this.commentMaxLength);
  readonly isCommentValid = computed(
    () => !this.isCommentTooShort() && !this.isCommentTooLong(),
  );
  readonly canConfirm = computed(() => this.selectedStar() > 0 && this.isCommentValid());
  readonly showCommentHint = computed(
    () => this.selectedStar() > 0 && this.commentLength() > 0 && !this.isCommentValid(),
  );

  onShow(): void {
    this.selectedStar.set(this.initialValue || 0);
    this.hoveredStar.set(0);
    this.comment.set(this.clampComment(this.initialComment || ''));
  }

  onCommentChange(value: string): void {
    this.comment.set(this.clampComment(value ?? ''));
  }

  private clampComment(value: string): string {
    return value.length > this.commentMaxLength
      ? value.slice(0, this.commentMaxLength)
      : value;
  }

  onStarHover(star: number): void {
    this.hoveredStar.set(star);
  }

  onStarLeave(): void {
    this.hoveredStar.set(0);
  }

  onStarClick(star: number): void {
    this.selectedStar.set(star);
  }

  confirm(): void {
    if (!this.canConfirm()) {
      return;
    }
    this.confirmed.emit({
      rating: this.selectedStar(),
      comment: this.trimmedComment(),
    });
    this.close();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.selectedStar.set(0);
    this.hoveredStar.set(0);
    this.comment.set('');
  }
}
